import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Colors } from 'modules/theme';
import H1 from 'components/H1';
import H4 from 'components/H4';
import H5 from 'components/H5';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import Col from 'components/Col';
import ListEmpty from 'components/ListEmpty';

const StatCard = styled.div`
  background: white;
  border-radius: ${props => props.borderRadius || '12px'};
  padding: 20px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  width: 100%;
  height: ${props => props.height || '120px'};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
  border: 1px solid #ecebf0;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${props =>
      props.onClick ? '0 4px 12px rgba(0, 0, 0, 0.1)' : '0 1px 6px rgba(0, 0, 0, 0.05)'};
  }
`;

// Grid container for pressure stats that handles responsive layout
export const PressureStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
  width: 100%;

  /* Connected card styling for horizontal layout */
  ${StatCard} {
    border-radius: 0;
    margin-bottom: 0;
    height: 120px;
    box-shadow: none;
    border: 1px solid #ecebf0;
    border-right: none;

    &:hover {
      box-shadow: none;
      background-color: #fafafa;
    }
  }

  ${StatCard}:first-child {
    border-radius: 12px 0 0 12px;
  }

  ${StatCard}:last-child {
    border-radius: 0 12px 12px 0;
    border-right: 1px solid #ecebf0;
  }

  @media (max-width: 1000px) {
    grid-template-columns: 1fr;

    /* Apply special styling when stats are stacked vertically */
    ${StatCard} {
      border-right: 1px solid #ecebf0;
      border-bottom: 1px solid #ecebf0;
    }

    ${StatCard}:first-child {
      border-radius: 12px 12px 0 0;
    }

    ${StatCard}:last-child {
      border-radius: 0 0 12px 12px;
      border-bottom: 1px solid #ecebf0;
    }
  }
`;

// More generic grid for multiple stat cards
export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  min-width: 0; /* Important for text overflow handling */
`;

const StatTitle = styled(H4)`
  color: ${Colors.gray8};
  margin: 0 0 8px 0;
  font-weight: 400;
  font-size: clamp(12px, 4vw, 16px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubTitle = styled.span`
  color: ${Colors.gray6};
  font-size: 12px;
  font-weight: 400;
  margin-left: 6px;
`;

const ValueContainer = styled.div`
  overflow: hidden;
  width: 100%;
`;

const StatValue = styled(H1)`
  font-size: clamp(22px, 5vw, 36px);
  line-height: 1;
  margin: 0;
  font-weight: 600;
  color: ${props => props.valueColor || Colors.black};
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StatPercent = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props =>
    props.increase ? 'rgba(39, 174, 96, 0.1)' : 'rgba(235, 87, 87, 0.1)'};
  color: ${props => (props.increase ? Colors.green : Colors.red)};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 14px;
  font-weight: 500;
  margin-left: 8px;
  vertical-align: middle;
`;

const StatPrevious = styled(H5)`
  color: ${Colors.gray7};
  margin: 6px 0 0 0;
  font-weight: 400;
  font-size: clamp(11px, 3vw, 14px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.bgColor || 'rgba(100, 161, 242, 0.1)'};
  margin-right: 16px;
  flex-shrink: 0;
`;

const DetailsLink = styled(H5)`
  color: ${Colors.lightBlue};
  text-decoration: none;
  margin-top: auto;
  padding-top: 5px;
  text-align: right;
  font-size: 14px;
`;

const QuickStats = ({
  icon,
  iconBgColor,
  iconColor,
  title,
  subtitle,
  value,
  unit,
  percentChange,
  previousValue,
  previousLabel = 'Previous week',
  isLoading,
  isInstalled = true,
  notInstalled, // For backwards compatibility
  onClick,
  borderRadius,
  className,
  valueColor,
  valueStyle,
  height,
}) => {
  const isPercentPositive = percentChange > 0;

  // Use notInstalled prop for backwards compatibility
  const showInstalled = notInstalled !== undefined ? !notInstalled : isInstalled;

  return (
    <StatCard onClick={onClick} borderRadius={borderRadius} className={className} height={height}>
      {isLoading ? (
        <Col justifyContent="center" alignItems="center" style={{ height: '100%', width: '100%' }}>
          <Loader size={30} />
        </Col>
      ) : !showInstalled ? (
        <Col justifyContent="center" style={{ width: '100%' }}>
          <ListEmpty message="Installation Not Complete" />
        </Col>
      ) : (
        <>
          <IconContainer bgColor={iconBgColor}>
            <Icon
              name={icon}
              color={iconColor || Colors.lightBlue}
              customStyle={{ fontSize: '20px' }}
            />
          </IconContainer>

          <ContentWrapper>
            <StatTitle>
              {title}
              {subtitle && <SubTitle>({subtitle})</SubTitle>}
            </StatTitle>

            <ValueContainer>
              <StatValue valueColor={valueColor} style={valueStyle}>
                {value !== null ? value : 'N/A'}
                {unit && value !== null ? ` ${unit}` : ''}
              </StatValue>
              {percentChange !== undefined && (
                <StatPercent increase={isPercentPositive}>
                  {isPercentPositive ? '+' : ''}
                  {percentChange}%
                </StatPercent>
              )}
            </ValueContainer>

            {previousValue !== undefined && (
              <StatPrevious>
                {previousLabel}: {previousValue}
              </StatPrevious>
            )}

            {onClick && <DetailsLink>Details →</DetailsLink>}
          </ContentWrapper>
        </>
      )}
    </StatCard>
  );
};

QuickStats.propTypes = {
  borderRadius: PropTypes.string,
  className: PropTypes.string,
  height: PropTypes.string,
  icon: PropTypes.string.isRequired,
  iconBgColor: PropTypes.string,
  iconColor: PropTypes.string,
  isInstalled: PropTypes.bool,
  isLoading: PropTypes.bool,
  notInstalled: PropTypes.bool, // For backwards compatibility
  onClick: PropTypes.func,
  percentChange: PropTypes.number,
  previousLabel: PropTypes.string,
  previousValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitle: PropTypes.string,
  title: PropTypes.string.isRequired,
  unit: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueColor: PropTypes.string,
  valueStyle: PropTypes.object,
};

export default QuickStats;
